import { UAParser } from 'ua-parser-js'
import { DeviceType, OSName } from 'ua-parser-js/enums'

const MOBILE_OS_NAMES = new Set([OSName.ANDROID, OSName.ANDROID_X86, OSName.IOS, OSName.KAIOS, OSName.UBUNTU_TOUCH, OSName.WINDOWS_MOBILE, OSName.WINDOWS_PHONE])

export async function isMobile() {
  const parser = new UAParser()
  const [result, featureCheckedDevice] = await Promise.all([parser.getResult().withClientHints(), parser.getDevice().withFeatureCheck()])
  const { os } = result
  const device = featureCheckedDevice

  // Modern iPads can identify their OS as macOS, so use UAParser's feature
  // detection result in addition to its normal OS and device parsing.
  const isIPad = device.vendor === 'Apple' && device.model === 'iPad'
  const hasMobileOS = MOBILE_OS_NAMES.has(os.name)
  const hasMobileDeviceType = device.type === DeviceType.MOBILE || device.type === DeviceType.TABLET

  return isIPad || hasMobileOS || hasMobileDeviceType
}

export async function RequestHandler(url, method = 'GET', body = {}, headers = {}, queryParams = {}) {
  if (!url) {
    throw new Error('url must be passed to RequestHandler')
  }

  const formattedUrl = new URL(url)

  // Params
  const allParams = Object.keys(queryParams)
  if (queryParams && allParams.length > 0) {
    allParams.forEach((key) => {
      formattedUrl.searchParams.append(key, queryParams[key])
    })
  }

  const options = {
    method,
  }

  // Headers
  if (headers && Object.keys(headers).length > 0) {
    headers['content-type'] = 'application/json'
    headers.Origin = window.location.origin

    options.headers = headers
  }

  // Body
  if (body && Object.keys(body).length > 0) {
    options.body = JSON.stringify(body)
  }

  const response = await fetch(formattedUrl.toString(), {
    ...options,
  })

  const json = await response.json()

  // error handling
  if (!response.ok) {
    if (response.status !== 200) {
      if (json.error && json.success === false) {
        const erro = Array.isArray(json.error.details) ? json.error.details?.join(' ') : json.error.details
        // throw new Error(erro)
        return erro
      } else if (json.error && json.message) {
        throw new Error(json.message)
      } else {
        // throw new Error(response.status)
        return response.status
      }
    }
  }

  // success handling
  if (json && json.data && json.success === true) {
    return json.data
  } else if (json && json.status === 200 && json.message != null) {
    return json.message
  }
}

export const parseJwt = (token) => {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join('')
  )

  return JSON.parse(jsonPayload)
}

export function capitalizeField(field) {
  return String(field || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase())
}

export function formatFieldValue(value, fieldName = '') {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  if (Array.isArray(value)) {
    return value.map((item) => formatFieldValue(item, fieldName)).join(', ')
  }

  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  const decodedValue = decodeDateFieldValue(value, fieldName)
  if (decodedValue) {
    return decodedValue
  }

  return String(value)
}

function decodeDateFieldValue(value, fieldName) {
  const normalizedValue = String(value)
  if (!/date|birth|expiry|expiration|issuance|time/i.test(fieldName)) {
    return ''
  }

  if (/^\d+$/.test(normalizedValue)) {
    return formatDateParts(parseDateString(normalizedValue)) || decodeNumericDateValue(normalizedValue)
  }

  return formatDateParts(parseDateString(normalizedValue))
}

function parseDateString(value) {
  const date = String(value || '').trim()
  if (!date) {
    return null
  }

  let day
  let month
  let year

  if (/^\d{8}$/.test(date)) {
    day = parseInt(date.slice(0, 2), 10)
    month = parseInt(date.slice(2, 4), 10) - 1
    year = parseInt(date.slice(4, 8), 10)
  } else {
    const separator = ['/', '-', '.'].find((item) => date.includes(item))
    if (!separator) {
      return null
    }

    const dateParts = date.split(separator).map((part) => part.trim())
    if (dateParts.length !== 3) {
      return null
    }

    day = parseInt(dateParts[0], 10)
    const monthMap = {
      JAN: 0,
      FEB: 1,
      MAR: 2,
      APR: 3,
      MAY: 4,
      JUN: 5,
      JUL: 6,
      AUG: 7,
      SEP: 8,
      OCT: 9,
      NOV: 10,
      DEC: 11,
      JANUARY: 0,
      FEBRUARY: 1,
      MARCH: 2,
      APRIL: 3,
      JUNE: 5,
      JULY: 6,
      AUGUST: 7,
      SEPTEMBER: 8,
      OCTOBER: 9,
      NOVEMBER: 10,
      DECEMBER: 11,
    }
    const monthPart = dateParts[1].toUpperCase()
    month = /^\d+$/.test(monthPart) ? parseInt(monthPart, 10) - 1 : monthMap[monthPart]
    year = parseInt(dateParts[2], 10)
  }

  if (isNaN(day) || day < 1 || day > 31 || isNaN(month) || month < 0 || month > 11 || isNaN(year) || year < 1000 || year > 9999) {
    return null
  }

  const testDate = new Date(year, month, day)
  if (testDate.getFullYear() !== year || testDate.getMonth() !== month || testDate.getDate() !== day) {
    return null
  }

  return { day, month, year }
}

function formatDateParts(dateParts) {
  if (!dateParts) {
    return ''
  }

  const month = String(dateParts.month + 1).padStart(2, '0')
  const day = String(dateParts.day).padStart(2, '0')
  return `${dateParts.year}-${month}-${day}`
}

function decodeNumericDateValue(value) {
  try {
    const bigValue = BigInt(value)
    const valueLength = String(value).length
    let timestamp

    if (valueLength >= 18) {
      timestamp = Number(bigValue / 1000000n)
    } else if (valueLength >= 15) {
      timestamp = Number(bigValue / 1000n)
    } else if (valueLength === 13) {
      timestamp = Number(bigValue)
    } else if (valueLength === 10) {
      timestamp = Number(bigValue * 1000n)
    } else {
      timestamp = Number(bigValue)
    }

    if (!Number.isSafeInteger(timestamp)) {
      return ''
    }

    const date = new Date(timestamp)
    if (date.getUTCFullYear() >= 1900 && date.getUTCFullYear() <= 2200) {
      return date.toISOString().slice(0, 10)
    }
  } catch {
    return ''
  }

  return ''
}
